import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { DocumentStorageService } from "../services/document-storage.service";
import { SavedDocument } from "../models/document-storage.types";
import { getLogger } from "../utils/logger";

const logger = getLogger("DocumentStorageContext");

interface DocumentStorageContextValue {
  documents: SavedDocument[];
  count: number;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

export const DocumentStorageContext = createContext<
  DocumentStorageContextValue | undefined
>(undefined);

interface DocumentStorageProviderProps {
  children: ReactNode;
}

/**
 * Global Document Storage Context Provider.
 *
 * Mirrors {@link UsageContext}: fetches the authenticated user's saved-document
 * list once per page load (only when authenticated) and caches it. Generation
 * forms call {@link refresh} after a successful generation so the "My documents"
 * tab is up to date on the next visit without a hard reload.
 *
 * The provider wraps the whole route tree, including public pages, so the
 * `isAuthenticated` guard below is load-bearing: anonymous visitors must never
 * trigger a storage API call (there is no token to send and nothing to list).
 *
 * @example
 * const { documents, count, loading, refresh } = useDocumentStorage();
 */
export const DocumentStorageProvider: React.FC<
  DocumentStorageProviderProps
> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [documents, setDocuments] = useState<SavedDocument[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshToken, setRefreshToken] = useState<number>(0);

  /**
   * Sequence number of the most recently started list request.
   *
   * Successive `refresh()` calls (e.g. deleting a document while a generation
   * that just finished also refreshes) can leave two `listDocuments` fetches in
   * flight. Responses are not guaranteed to arrive in the order they were
   * requested, so each request captures the sequence it was issued with and
   * only commits state while it is still the latest — otherwise a slower, older
   * response would overwrite fresher data and clear `loading` prematurely.
   */
  const latestRequestId = useRef(0);

  const fetchDocuments = useCallback(async () => {
    if (!isAuthenticated) {
      // Bump the sequence so any request still in flight from a signed-in
      // session cannot repopulate the list after sign-out.
      latestRequestId.current += 1;
      setDocuments([]);
      setError(null);
      setLoading(false);
      return;
    }

    latestRequestId.current += 1;
    const requestId = latestRequestId.current;
    const isStale = () => requestId !== latestRequestId.current;

    setLoading(true);
    setError(null);

    try {
      const accessToken = await getAccessTokenSilently();
      const response = await DocumentStorageService.listDocuments(accessToken);

      if (response.error || !response.data) {
        throw new Error(response.error?.message || "Failed to fetch documents");
      }

      if (isStale()) {
        return;
      }
      setDocuments(response.data.documents);
    } catch (err) {
      const fetchError =
        err instanceof Error
          ? err
          : new Error("Unknown error fetching documents");
      logger.error("Error fetching documents", fetchError);
      if (isStale()) {
        return;
      }
      setError(fetchError);
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, getAccessTokenSilently, refreshToken]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const refresh = useCallback(() => {
    setRefreshToken((prev) => prev + 1);
  }, []);

  const value: DocumentStorageContextValue = {
    documents,
    count: documents.length,
    loading,
    error,
    refresh,
  };

  return (
    <DocumentStorageContext.Provider value={value}>
      {children}
    </DocumentStorageContext.Provider>
  );
};
